/**
 * An email given as a way to reach somebody, not (yet) as a sign-in
 * (well/product/33 §4).
 *
 * The soft save (lib/account.ts) sends a sign-in link and only writes `email`
 * once the link proves it. Inside Facebook's or Instagram's in-app browser that
 * link opens in Mail and then Safari: a different browser, with different
 * storage. There the finish step makes a *new* anonymous account and links the
 * email to it, while everything made in Facebook's browser stays with the old
 * one. So an email typed there must be kept against the uid that is holding
 * the work, the moment it is typed, whether or not a link is ever tapped.
 *
 * The link is sent from in there too now: it carries a handoff code for this
 * uid (lib/handoff.ts), so the browser Mail opens it in signs in as the same
 * person before anything else and the email is linked to the work in place.
 * When no code can be minted, saveWithEmail sends nothing from an in-app
 * browser rather than a link that would land on a stranger.
 *
 * It goes on the person's own user doc as `contactEmail`. The rules let anyone
 * write any field of their own doc but `email` and `credits` (firestore.rules),
 * and only they can read it. It is unverified, so the reminder cron sends it one
 * email and no more: Day 2, with a sign-in link that confirms it (backend
 * kindling-reminders.service contactCue).
 */
import { writeUserDoc } from './feed';
import { saveWithEmail } from './account';
import { inAppBrowser } from './in-app';

export interface ContactOutcome { kept: boolean; linkSent: boolean; inApp: string | null }

/** The address last given on this device, so a later ask can offer it back instead of asking again. */
const LAST_KEY = 'kindling.contactEmail';
export function lastContactEmail(): string {
  try { return localStorage.getItem(LAST_KEY) || ''; } catch { return ''; }
}

/**
 * Keep it on this person's own doc, and send the soft save's link, which brings
 * this same account back wherever it is opened. Never throws: a failure is reported
 * in the outcome, and the person is never held on the screen by it.
 */
export async function keepContactEmail(email: string, from: string, ua?: string): Promise<ContactOutcome> {
  const clean = email.trim().slice(0, 200);
  const inApp = inAppBrowser(ua);
  try { localStorage.setItem(LAST_KEY, clean); } catch { /* ignore */ }
  // side by side: neither waits on the other, and a screen waiting on both waits for the slower only
  const [kept, linkSent] = await Promise.all([
    writeUserDoc({ contactEmail: clean, contactEmailFrom: from, contactEmailAt: Date.now() })
      .then(() => true, (err) => { console.warn('[kindling] keeping the contact email failed', err); return false; }),
    saveWithEmail(clean).catch(() => 'failed' as const).then((out) => out === 'email_sent'),
  ]);
  return { kept, linkSent, inApp };
}
